import React, { useEffect, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import axios from "axios";
import { API_ENDPOINT } from "../../../../../../lib/api-url";

export default function AlphaWolvesDisplayComponent() {
  const [team, setTeam] = useState(null);

  const logoCtrl = useAnimation();
  const svgCtrl = useAnimation();
  const pairCtrls = [useAnimation(), useAnimation(), useAnimation()];

  const fetchListOfTeams = async () => {
    try {
      const result = await axios.get(API_ENDPOINT.wallTeams.getIndex);
      const teams = result?.data?.data || [];
      const alpha = teams.find((t) => t.name === "ALPHAWOLVES");
      if (alpha) setTeam(alpha);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchListOfTeams();
  }, []);

  useEffect(() => {
    (async () => {
      await logoCtrl.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" },
      });
      await svgCtrl.start("centerConnector");
      await pairCtrls[2].start("show");
      await svgCtrl.start("horizFromCenter");
      await pairCtrls[0].start("show");
      await pairCtrls[1].start("show");
    })();
  }, []);

  const pos = [0, 192.5, 385, 577.5, 770];
  const vb = { w: 770, h: 26 };
  const yLine = 10;
  const cx = vb.w / 2;
  const edgeH = 11;
  const ballR = 1.9;

  const mkLine = (x1, y1, x2, y2) => `M ${x1} ${y1} L ${x2} ${y2}`;

  const svgVariants = {
    centerConnector: {
      strokeDashoffset: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeInOut" },
    },
    horizFromCenter: {
      transition: { duration: 0.8, ease: "easeInOut" },
    },
  };

  const edgeAnimForIndex = (i) => {
    if (i === 4 || i === 0) return pairCtrls[0];
    if (i === 3 || i === 1) return pairCtrls[1];
    return pairCtrls[2];
  };

  const players = React.useMemo(() => {
    if (!team) return new Array(5).fill(null);
    const slots = new Array(5).fill(null);
    if (team.captain) slots[2] = { user: team.captain, isCaptain: true };
    (team.members || []).forEach((m, idx) => {
      const slot = idx < 2 ? idx : idx + 1;
      if (slot < 5) slots[slot] = { user: m.user, isCaptain: false };
    });
    return slots;
  }, [team]);

  const displayName = (player) => {
    const u = player?.user || null;
    if (!u) return "";
    const fn = u.first_name || "";
    const ln = u.last_name || "";
    return (fn + " " + ln).trim();
  };

  return (
    <div
      className="w-full h-full flex items-center justify-center flex-col text-white overflow-visible"
      style={{ overflow: "visible", scale: "1.5" }}
    >
      <motion.div
        className="team-logo-cotnainer relative w-full"
        initial={{ opacity: 0, y: 8 }}
        animate={logoCtrl}
      >
        <div className="w-full flex items-center flex-col justify-center">
          <img
            src="/teams/alphawolves-logo.webp"
            className="w-[220px] bottom-[-2px] left-[-12px]"
            alt="banner"
          />
          <span
            style={{ fontFamily: "Etemad" }}
            className="text-[24px] text-nowrap text-[#6CD0FF] font-semibold"
          >
            Alpha Wolves
          </span>
        </div>
      </motion.div>

      <div
        className="relative w-[770px] mt-[38px] overflow-visible"
        style={{ overflow: "visible" }}
      >
        <svg
          viewBox={`0 0 ${vb.w} ${vb.h}`}
          className="w-[770px] h-[30px] overflow-visible"
          preserveAspectRatio="none"
        >
          <path d={mkLine(0, yLine, vb.w, yLine)} fill="none" stroke="none" />
          <defs>
            <clipPath id="clipRight">
              <motion.rect
                initial={{ x: cx, width: 0, y: 0, height: vb.h }}
                animate={svgCtrl}
                variants={{
                  horizFromCenter: {
                    x: cx,
                    width: vb.w - cx,
                    transition: { duration: 0.8, ease: "easeInOut" },
                  },
                }}
              />
            </clipPath>
            <clipPath id="clipLeft">
              <motion.rect
                initial={{ x: cx, width: 0, y: 0, height: vb.h }}
                animate={svgCtrl}
                variants={{
                  horizFromCenter: {
                    x: 0,
                    width: cx,
                    transition: { duration: 0.8, ease: "easeInOut" },
                  },
                }}
              />
            </clipPath>
          </defs>

          <motion.path
            d={mkLine(cx, 0, cx, yLine)}
            stroke="#FFFFFF"
            strokeWidth={0.7}
            strokeDasharray="2 2"
            strokeLinecap="butt"
            vectorEffect="non-scaling-stroke"
            initial={{ strokeDashoffset: yLine, opacity: 0 }}
            animate={svgCtrl}
            variants={svgVariants}
            style={{ willChange: "stroke-dashoffset, opacity" }}
          />

          <path
            d={mkLine(cx, yLine, vb.w, yLine)}
            stroke="#FFFFFF"
            strokeWidth={0.7}
            strokeDasharray="8 6"
            strokeLinecap="butt"
            clipPath="url(#clipRight)"
          />
          <path
            d={mkLine(cx, yLine, 0, yLine)}
            stroke="#FFFFFF"
            strokeWidth={0.7}
            strokeDasharray="8 6"
            strokeLinecap="butt"
            clipPath="url(#clipLeft)"
          />

          {pos.map((p, i) => (
            <React.Fragment key={`edge-${i}`}>
              <motion.path
                d={mkLine(p, yLine, p, yLine + edgeH)}
                stroke="#FFFFFF"
                strokeWidth={0.7}
                strokeDasharray="2 2"
                strokeLinecap="butt"
                vectorEffect="non-scaling-stroke"
                initial={{ strokeDashoffset: edgeH, opacity: 0 }}
                animate={edgeAnimForIndex(i)}
                variants={{
                  show: {
                    strokeDashoffset: 0,
                    opacity: 1,
                    transition: { duration: 0.4, ease: "easeInOut" },
                  },
                }}
                style={{ willChange: "stroke-dashoffset, opacity" }}
              />
              <motion.circle
                cx={p}
                cy={yLine + edgeH + (ballR + 0.3)}
                r={ballR}
                fill="#FFFFFF"
                initial={{ scale: 0, opacity: 0 }}
                animate={edgeAnimForIndex(i)}
                variants={{
                  show: {
                    scale: 1,
                    opacity: 1,
                    transition: {
                      delay: 0.35,
                      duration: 0.25,
                      ease: "backOut",
                    },
                  },
                }}
              />
            </React.Fragment>
          ))}
        </svg>
      </div>

      <div
        className="w-full flex items-center justify-center overflow-visible"
        style={{ overflow: "visible" }}
      >
        <div
          className="relative w-[770px] h-fit mt-[38px] grid grid-cols-5 place-items-center gap-x-40 overflow-visible"
          style={{ overflow: "visible" }}
        >
          {players.map((player, i) => (
            <motion.div
              key={`card-${i}`}
              className="team-logo-cotnainer relative place-self-center"
              initial="hidden"
              animate={edgeAnimForIndex(i)}
              variants={{
                hidden: { opacity: 0, y: 8 },
                show: {
                  opacity: 1,
                  y: 0,
                  transition: { delay: 0.5, duration: 0.35, ease: "easeOut" },
                },
              }}
            >
              {i === 2 && (
                <div className="absolute z-[50] right-0 top-[-4px] w-[33px]">
                  <img src="/teams/captains-badge.svg" alt="captain" />
                </div>
              )}
              <img
                className="w-[66px] absolute left-1/2 translate-x-[-50%] top-[-14px]"
                src={player?.user?.avatar_url || "/avatar/avatar-default.webp"}
                alt={displayName(player) || "avatar"}
              />
              <img src="/teams/alphawolves-player-box.svg" alt="player-frame" />
              <div className="flex items-center w-[142px]">
                <img
                  src="/teams/team-banner.svg"
                  className="absolute bottom-[-2px] left-[-14px] z-20"
                  alt="banner"
                />
                <img
                  src="/teams/team-white.svg"
                  className="absolute w-[78px] z-10 bottom-[-18px]"
                  alt=""
                />
                <span className="absolute bottom-[2px] left-[12px] z-40 w-[160px] truncate text-[12px] text-[#FFFFFF] font-semibold">
                  {displayName(player)}
                </span>
                <span className="text-[12px] bottom-[-18px] right-[92px] absolute z-[40] text-[#010016] font-semibold truncate max-w-[120px]">
                  رتبه 1
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
